import { campaignTypes } from "@/src/constants/campaignConstants.js";
import StatusTagProvider from "@/src/components/StatusTagProvider.vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import imagePath from "@/src/paths/imagePath.js";
import LocalTimeColumn from "@/src/components/formatting_components/LocalTimeColumn.vue";
import { commonDateOptions } from "@/src/constants/filterConstants.js";
import { callStatusOptions } from "@/src/constants/actionOptionsConstants.js";

export const audioDeviceErrors = {
  NOT_ALLOWED: "NotAllowedError",
  PERMISSION_DENIED: "PermissionDeniedError",
  NOT_FOUND: "NotFoundError",
  NOT_READABLE: "NotReadableError",
};

export const audioDeviceErrorMessages = {
  [audioDeviceErrors.NOT_ALLOWED]:
    "Microphone permission denied - rejecting call",
  [audioDeviceErrors.PERMISSION_DENIED]:
    "Microphone permission denied - rejecting call",
  [audioDeviceErrors.NOT_FOUND]:
    "Microphone not available - check device settings",
  [audioDeviceErrors.NOT_READABLE]:
    "Microphone not available - check device settings",
};

export const consentTimeouts = {
  VOICE_AGENT_CONSENT: 300000,
};

export const dialerDigits = [
  "1",
  "2",
  "3",
  "4",
  "5",
  "6",
  "7",
  "8",
  "9",
  "*",
  "0",
  "#",
];

export const callActionsCSS = {
  VARIANT1: "variant1",
  VARIANT2: "variant2",
};

export const callActionsList = {
  DIALER: "dialer",
  MUTE: "mute",
  HOLD: "hold",
  NOTE: "note",
  RECORD: "record",
  TRANSFER: "transfer",
  SWAP: "swap",
  MERGE: "merge",
  VOICEMAIL_DROP: "voicemail_drop",
};

export const callActionModals = {
  [callActionsList.DIALER]: {
    title: "Dialer",
    width: 250,
    positionKey: "dialer",
  },
  [callActionsList.NOTE]: {
    title: "Add Note",
    width: 250,
    positionKey: "note",
  },
  [callActionsList.TRANSFER]: {
    title: "Transfer Call",
    width: 280,
    positionKey: "transfer",
  },
  [callActionsList.VOICEMAIL_DROP]: {
    title: "Voicemail Drop",
    width: 350,
    positionKey: "voicemailDrop",
  },
};

export const callActions = [
  {
    id: callActionsList.DIALER,
    text: "Dialer",
    selected: imageNames.DIALER_SELECTED,
    unselected: imageNames.DIALER_UNSELECTED,
    size: 16,
    style: callActionsCSS.VARIANT1,
  },
  {
    id: callActionsList.NOTE,
    text: "Note",
    selected: imageNames.DIALER_NOTE_SELECTED,
    unselected: imageNames.DIALER_NOTE_UNSELECTED,
    size: 16,
    style: callActionsCSS.VARIANT1,
  },
  {
    id: callActionsList.MUTE,
    text: "Mute",
    active: "Muted",
    selected: imageNames.MUTE_SELECTED,
    unselected: imageNames.MUTE_UNSELECTED,
    size: 16,
    style: callActionsCSS.VARIANT1,
  },
  // {
  //   id: callActionsList.HOLD,
  //   text: "Hold",
  //   selected: imageNames.holdSelectedIcon,
  //   unselected: imageNames.holdUnselectedIcon,
  //   size: 16,
  //   style: callActionsCSS.VARIANT1,
  // },
  {
    id: callActionsList.RECORD,
    text: "Record",
    active: "Recording",
    selected: imageNames.RECORD_SELECTED,
    unselected: imageNames.RECORD_UNSELECTED,
    size: 16,
    style: callActionsCSS.VARIANT2,
  },
  {
    id: callActionsList.TRANSFER,
    text: "Transfer",
    selected: imageNames.TRANSFER_SELECTED,
    unselected: imageNames.TRANSFER_UNSELECTED,
    size: 12,
    style: callActionsCSS.VARIANT1,
  },
  {
    id: callActionsList.SWAP,
    text: "Swap",
    active: "Swapping",
    icon: imageNames.CALL_SWAP,
    size: 16,
    style: callActionsCSS.VARIANT1,
  },
  {
    id: callActionsList.MERGE,
    text: "Merge",
    active: "Merging",
    icon: imageNames.CALL_SWAP,
    size: 16,
    style: callActionsCSS.VARIANT1,
  },
  {
    id: callActionsList.VOICEMAIL_DROP,
    text: "Voicemail Drop",
    selected: imageNames.VOICEMAIL_DROP_SELECTED,
    unselected: imageNames.VOICEMAIL_DROP_UNSELECTED,
    size: 16,
    style: callActionsCSS.VARIANT1,
  },
];

export const callStatuses = {
  IDLE: "idle",
  PENDING: "pending",
  RINGING: "ringing",
  CONNECTING: "connecting",
  CONNECTED: "connected",
  OPEN: "open",
  RECONNECTING: "reconnecting",
  CLOSED: "closed",
  INPROGRESS: "in-progress",
};

export const defaultCallValues = {
  CAMPAIGN_CALL_NAME: "Campaign Call",
  DEFAULT_CAMPAIGN_ID: 0,
};

export const callLogStatuses = {
  INITIATED: "Initiated",
  COMPLETED: "Completed",
  BUSY: "Busy",
  FORWARDED: "Forwarded",
  FAILED: "Failed",
  VOICEMAIL: "Voicemail",
  NO_ANSWER: "No Answer",
};

export const callRoutesHeaders = [
  { label: "Route Name", key: "name", width: 4 },
  { label: "Routed Number", key: "phone_number", width: 4 },
];

export const callDirections = {
  INCOMING: "Incoming",
  OUTGOING: "Outgoing",
};

export const callLogDirections = {
  INBOUND: "inbound",
  OUTBOUND: "outbound",
};

export const numberOwnerTypes = {
  USER: "User",
  TEAM: "Team",
};

export const dialerTypes = {
  POWER_DIALER: "power",
  PARALLEL_DIALER: "parallel",
  VOICE_AGENTS_DIALER: "voice_agents",
};

export const callSettingKeys = {
  RECORD_CALL: "record_call",
  AUTO_MACHINE_DETECTION: "auto_machine_detection",
  CALL_TRANSCRIPT: "call_transcript",
  DISPOSITION_MANDATORY: "disposition_mandatory",
  DNC: "dnc",
  POST_CALL_FORM_AUTO_OPEN: "post_call_form_auto_open",
};

export const settingValues = {
  TRUE: "true",
  FALSE: "false",
};

export const dialerSettingsConfig = {
  [callSettingKeys.AUTO_MACHINE_DETECTION]: {
    title: "Auto machine detection",
    description: "AI detects human vs voicemail in the first seconds and auto-labels the call. Side note accuracy may drop with long greetings IVRs or background noise",
  },
  [callSettingKeys.CALL_TRANSCRIPT]: {
    title: "Call transcription",
    description: "Automatically transcribe all calls for better insights and record-keeping",
  },
  [callSettingKeys.POST_CALL_FORM_AUTO_OPEN]: {
    title: "Post-call details form",
    description: "Automatically open the post-call details form as soon as a call ends in power dialer campaign",
  },
};

export const callLogStatusIcons = {
  Initiated: { icon: imageNames.GOING_CALL },
  Busy: { icon: imageNames.BUSY_CALL },
  Completed: { icon: imageNames.GOING_CALL },
  Skipped: { icon: imageNames.GOING_CALL },
  Forwarded: { icon: imageNames.FORWARD_CALL },
  Failed: { icon: imageNames.BUSY_CALL },
  DNC: { icon: imageNames.BUSY_CALL },
  "Voicemail Dropped": { icon: imageNames.VOICEMAIL_CALL },
  Voicemail: { icon: imageNames.VOICEMAIL_CALL },
  "No Answer": { icon: imageNames.BUSY_CALL },
  Transferred: { icon: imageNames.TRANSFER_CALL },
  Dialing: { icon: imageNames.GOING_CALL },
  Cancelled: { icon: imageNames.CANCELLED_CALL },
};

export const callCampaignDialerTypeOptions = [
  {
    id: dialerTypes.POWER_DIALER,
    title: "Power Dialer",
    description:
      "Power dialer dials one prospect at a time, connecting you only if the prospect picks up, minimising disconnected calls.",
    icon: imageNames.POWER_DIALER_GREY,
  },
  {
    id: dialerTypes.PARALLEL_DIALER,
    title: "Parallel Dialer",
    description:
      "Parallel Dialer dials multiple prospects simultaneously, connecting you only if the prospect picks up, minimising downtime.",
    icon: imageNames.PARALLEL_DIALER_GREY,
  },
];

export const callCampaignAudienceTypeOptions = [
  {
    id: campaignTypes.MANUAL_LIST,
    title: "Select a list",
    description: "Select an existing list",
    icon: imageNames.ADD_LIST_GREY,
    alt: "list_icon",
  },
  {
    id: campaignTypes.MANUAL,
    title: "Upload leads",
    description: "Provide a CSV of leads",
    icon: imageNames.UPLOAD_GREY,
    alt: "upload_icon",
  },
];

export const callsTableHeaders = [
  { label: "Name", key: "name", width: 200 },
  { label: "Status", key: "call_status", width: 200 },
  { label: "Total Contacts", key: "total_contacts", width: 200 },
  { label: "Answered", key: "answered_calls", width: 200 },
  { label: "Skipped", key: "skipped_calls", width: 200 },
  {
    label: "Created by",
    key: "created_by",
    width: 200,
    avatar: "profile_icon",
    view: true,
  },
  { label: "Created On", key: "created_at", width: 200 },
];

export const callViewTableHeaders = [
  { label: "Name", key: "name", width: 200, avatar: "profile_pic", sortable: true, ellipsis: false },
  { label: "Number", key: "phone_number", width: 150, sortable: true, ellipsis: false },
  { label: "Status", key: "call_status", width: 150, sortable: true, ellipsis: false },
  { label: "Disposition", key: "disposition", width: 150, sortable: true, ellipsis: false },
  {
    label: "Company Name",
    key: "current_company",
    width: 200,
    avatar: "company_logo",
    view: true,
    sortable: true,
    ellipsis: false,
  },
  { label: "Current Role", key: "current_role", width: 180, sortable: true, ellipsis: false },
  { label: "Timezone", key: "timezone", width: 180, sortable: true, ellipsis: false },
  { label: "Local Time", key: "local_time", width: 120, ellipsis: false },
];

export const voicemailTableHeaders = [
  { label: "Name", key: "name", width: 4 },
  { label: "Added by", key: "added_by", width: 4 },
  { label: "Voicemail", key: "source", width: 4 },
];

export const callCampaignButton = {
  draft: {
    text: "Start Campaign",
    action: "start",
  },
  active: {
    text: "Pause Campaign",
    icon: imagePath.pauseIconGrey,
    action: "pause",
  },
  paused: {
    text: "Resume Campaign",
    icon: imagePath.dark.action.resumeIconBlack,
    action: "resume",
  },
  completed: {
    text: "",
    action: null,
  },
};

export const callCampaignStatuses = {
  DRAFT: "draft",
  IN_PROGRESS: "in_progress",
  RESUME: "resume",
  PAUSED: "paused",
  COMPLETED: "completed",
  ENDED: "ended",
};

export const assignCallTypes = {
  USER: "user",
  TEAM: "team",
  PHONE_NUMBER: "phone_number",
};

export const assignCallTypesList = {
  USER: { value: "user", label: "User" },
  TEAM: { value: "team", label: "Team" },
  PHONE_NUMBER: { value: "phone_number", label: "Phone Number" },
};

export const conferenceParticipantLabels = {
  CALLER: "caller",
  ORIGINAL_AGENT: "original_agent",
  TRANSFER_TARGET: "transfer_target",
};

export const callCampaignsCustomColumnRender = {
  get call_status() { return StatusTagProvider; },
  local_time: {
    component: LocalTimeColumn,
    props: (record) => ({
      timezone: record.timezone,
    }),
  },
};

export const validationEntities = {
  NORMAL_CALL: "normal_call",
  TRANSFER_CALL: "transfer_call",
};

export const voiceAgents = [
  { id: 1, text: "Aura Stella" },
  { id: 2, text: "Aura Asteria" },
  { id: 3, text: "Aura Luna" },
  { id: 4, text: "Aura Arcas" },
];

export const getPhoneNumberHeaders = (isForwardingEnabled) => {
  if (isForwardingEnabled) {
    return [
      { label: "Number", key: "phone_number", width: 2, prefix: "flag" },
      { label: "Name", key: "name", width: 2 },
      { label: "Assigned To", key: "assigned_to", width: 2, view: true },
      { label: "Type", key: "type", width: 2 },
      { label: "Call forwarding", key: "is_forwarding_enabled", width: 2 },
      {
        label: "Forward to",
        key: "forwarding_number",
        width: 2,
        prefix: "flag",
      },
    ];
  } else {
    return [
      { label: "Number", key: "phone_number", width: 3, prefix: "flag" },
      { label: "Name", key: "name", width: 3 },
      { label: "Assigned To", key: "assigned_to", width: 3, view: true },
      { label: "Type", key: "type", width: 3 },
    ];
  }
};

export const phoneBlockListHeaders = [
  { label: "Name", key: "name", width: 2, avatar: "profile_pic", view: true },
  { label: "Number", key: "number", width: 2 },
  { label: "DND status", key: "dnd_status", width: 2 },
  { label: "Updated on", key: "updated_on", width: 2 },
  {
    label: "Updated by",
    key: "updated_by",
    width: 2,
    avatar: "profile_icon",
    view: true,
  },
];

export const dndStatus = {
  DO_NOT_CALL: "do_not_call",
  WHITELIST: "whitelist",
};

export const dndStatusOptions = [
  { value: dndStatus.DO_NOT_CALL, label: "Do Not Call" },
  { value: dndStatus.WHITELIST, label: "Whitelist" },
];

export const dndStatusDisplayMap = {
  [dndStatus.DO_NOT_CALL]: "Do Not Call",
  [dndStatus.WHITELIST]: "Whitelist",
};

export const phoneBlockListCustomColumnRender = {
  get dnd_status() { return StatusTagProvider; },
};

export const phoneCheckStatus = {
  IDLE: "idle",
  CHECKING: "checking",
  COMPLETE: "complete",
};

export const callLogFilters = [
  {
    id: "status",
    name: "Status",
    options: [
      { id: "voicemail", name: "Voicemail" },
      { id: "voicemail-dropped", name: "Voicemail Dropped" },
      { id: "failed", name: "Failed" },
      { id: "no-answer", name: "No Answer" },
      { id: "busy", name: "Busy" },
      { id: "cancelled", name: "Cancelled" },
      { id: "transferred", name: "Transferred" },
      { id: "completed", name: "Completed" },
    ],
    searchable: true,
    multiSelect: true,
    showInitials: true,
  },
  {
    id: "disposition",
    name: "Disposition",
    options: [],
    searchable: true,
    multiSelect: true,
    showInitials: true,
  },
];

export const callTooltipContent = {
  OUTBOUND_CALLER_ID:
    "Set the number shown on outbound calls. Once verified, caller ID works in most countries, but some countries might require separate verification.",
  EXTENSION:
    "If calls to you go through an automated menu or require an extension, enter the extension digits here. Leave blank if none.",
};

export const conversationIntelligenceOutputTypes = {
  BOOLEAN: "boolean",
  PERCENTAGE: "percentage",
  NUMBER: "number",
  TEXT: "text",
};

export const conversationIntelligenceOutputTypeOptions = [
  {
    value: conversationIntelligenceOutputTypes.TEXT,
    label: "Text",
    icon: imageNames.TEXT_EDITOR_GREY,
  },
  {
    value: conversationIntelligenceOutputTypes.BOOLEAN,
    label: "Boolean",
    icon: imageNames.BOOLEAN_GREY,
  },
  {
    value: conversationIntelligenceOutputTypes.PERCENTAGE,
    label: "Percentage",
    icon: imageNames.PERCENTAGE_GREY,
  },
  {
    value: conversationIntelligenceOutputTypes.NUMBER,
    label: "Number",
    icon: imageNames.NUMBER_GREY,
  },
];

export const callLogsTableHeaders = [
  {
    label: "Contact/Lead",
    key: "entity_name",
    width: 2,
    avatar: "entity_profile_pic",
    view: true,
  },
  {
    label: "Contact/Lead number",
    key: "contact_number",
    width: 2,
  },
  {
    label: "SuperAGI user",
    key: "user_name",
    width: 2,
    view: true,
    avatarDataKey: "superagi_user",
    avatarParam: "name",
  },
  {
    label: "SuperAGI user number",
    key: "your_number",
    width: 2,
  },
  {
    label: "Direction",
    key: "direction",
    width: 1,
  },
  {
    label: "Call status",
    key: "status",
    width: 2,
  },

  {
    label: "Disposition",
    key: "disposition",
    width: 2,
  },
  {
    label: "Duration",
    key: "duration_formatted",
    width: 1,
  },
  {
    label: "Campaign Name",
    key: "campaign_name",
    width: 2,
  },
  {
    label: "Last activity",
    key: "updated_at_formatted",
    width: 2,
  },
  {
    label: "Call Placed on",
    key: "created_at_formatted",
    width: 2,
  },
];

export const callLogsFiltersOptions = [
  {
    id: "status",
    name: "Status",
    type: "select",
    options: callStatusOptions.map(status => ({ id: status.value, name: status.label })),
    searchable: false,
    multiSelect: true,
    showInitials: false,
  },
  {
    id: "user_id",
    name: "User",
    type: "user",
    options: [],
    searchable: true,
    multiSelect: true,
    showInitials: true,
  },
  {
    id: "campaign_id",
    name: "Campaign",
    type: "user",
    options: [],
    searchable: true,
    multiSelect: false,
    showInitials: false,
    searchOptions: async (query) => {
      const { fetchCampaigns } = await import("@/src/utils/callsUtils.js");
      const res = await fetchCampaigns(
        { page: 1, pageSize: 50, searchQuery: query || "", filters: [] },
        null,
      );
      return (res?.campaigns || []).map((c) => ({
        id: c.id,
        name: c.name || `Campaign ${c.id}`,
      }));
    },
  },
  {
    id: "created_date",
    name: "Call Placed on",
    options: commonDateOptions,
    searchable: false,
    multiSelect: false,
    showInitials: false,
  },
  {
    id: "disposition",
    name: "Disposition",
    type: "select",
    options: [],
    searchable: true,
    multiSelect: true,
    showInitials: false,
  },
  {
    id: "direction",
    name: "Direction",
    type: "select",
    options: [
      { id: "inbound", name: "Incoming" },
      { id: "outbound", name: "Outgoing" },
    ],
    searchable: false,
    multiSelect: false,
    showInitials: false,
  },
  {
    id: "duration",
    name: "Call Duration",
    type: "select_timestamp",
    datatype: "select_timestamp",
    searchable: false,
    multiSelect: false,
    showInitials: false,
  },
];

export const callLogDetailTabs = [
  { title: "Recording & Transcript" },
  { title: "Call Intelligence" },
  { title: "Notes" },
];

export const callLogDropdownItems = [
  {
    key: "add_to_dnc",
    action: null,
    icon: imageNames.NOT_ALLOWED_GREY,
    text: "Add to DNC",
  },
];

export const ciTemplateActions = [
  {
    label: "Edit",
    handler: null,
    icon: imageNames.EDIT,
  },
  {
    label: "Set as Primary",
    handler: null,
    icon: imageNames.STAR_GREY,
    condition: (row) => !row.is_primary,
  },
  {
    label: "Delete",
    handler: null,
    icon: imageNames.DELETE_GREY,
  },
];

export const ringtoneFileConfig = {
  allowedExtensions: [".mp3", ".wav"],
  allowedMimeTypes: [
    "audio/mpeg",
    "audio/mp3",
    "audio/wav",
    "audio/wave",
    "audio/x-wav",
  ],
  maxFileSizeBytes: 10 * 1024 * 1024,
};

export const callLogDetailSections = [
  {
    fields: [
      { label: "Contact/Lead Phone Number", key: "entityPhoneNumber" },
      { label: "SuperAGI Number", key: "superAGIPhoneNumber" },
      { label: "SuperAGI User", key: "superAGIUser" },
      { label: "Direction", key: "call_direction" },
      { label: "Outcome", key: "call_status" },
    ],
  },
  {
    fields: [
      { label: "Duration", key: "duration", format: "time" },
      { label: "Call placed on", key: "created_at", format: "dateTime" },
      { label: "Last Activity", key: "updated_at", format: "dateTime" },
    ],
  },
];
